import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import BackButton from "../components/BackButton";
import Navbar from "../components/Navbar";
import { CRICKET_API_URL } from "../config";
import { ContestContext } from "../context/ContestContext";
import { UserContext } from "../context/UserContext";
import {
    getPlayerById,
    getPlayerId,
    normalizeTeamSelection,
} from "../utils/playerIdentity";
import {
    getResolvedLineupStatus,
    isLineupOutForMatch,
} from "../utils/playerLineupStatus";

const DEFAULT_PLAYER_IMAGE = "https://cdn-icons-png.flaticon.com/512/149/149071.png";

const handlePlayerImageError = (event) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = DEFAULT_PLAYER_IMAGE;
};

const parseMatchTime = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const normalizedValue = value < 10000000000 ? value * 1000 : value;
        const parsed = new Date(normalizedValue);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed;

        const fallback = new Date(value.replace(" ", "T"));
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    return null;
};

const getRoleCounts = (players = []) => {
    const counts = { WK: 0, BAT: 0, AR: 0, BOWL: 0 };

    (players || []).forEach((player) => {
        const role = String(player?.role || "").toUpperCase();

        if (role.includes("WK") || role.includes("KEEPER")) counts.WK += 1;
        else if (role.includes("BAT")) counts.BAT += 1;
        else if (role.includes("AR") || role.includes("ALL")) counts.AR += 1;
        else if (role.includes("BOWL")) counts.BOWL += 1;
    });

    return counts;
};

const normalizePlayerName = (value = "") =>
    String(value || "").trim().toLowerCase();

const normalizeTeamCode = (value = "") =>
    String(value || "").trim().toUpperCase();

const buildLineupLookupKey = (player = {}) =>
    `${normalizePlayerName(player?.name)}::${normalizeTeamCode(player?.team)}`;

const getMatchedLiveLineupPlayer = (player = {}, liveLineupLookup = {}) => {
    const playerId = getPlayerId(player);
    if (playerId && liveLineupLookup?.byId?.[playerId]) {
        return liveLineupLookup.byId[playerId];
    }

    const lookupKey = buildLineupLookupKey(player);
    if (lookupKey && liveLineupLookup?.byKey?.[lookupKey]) {
        return liveLineupLookup.byKey[lookupKey];
    }

    const normalizedName = normalizePlayerName(player?.name);
    if (normalizedName && liveLineupLookup?.byName?.[normalizedName]) {
        return liveLineupLookup.byName[normalizedName];
    }

    return null;
};

/* =========================================
   ✅ COMPONENT: Match Timer (Contests.jsx)
========================================= */
const MatchTimer = ({ startTime }) => {
    const [timeLeft, setTimeLeft] = useState("");

    useEffect(() => {
        const calculateTimeLeft = () => {
            if (!startTime) return "TBD";

            try {
                const parsedMatchTime = parseMatchTime(startTime);
                const start = parsedMatchTime?.getTime();
                const now = new Date().getTime();

                if (!start || Number.isNaN(start)) return "TBD";

                const diff = start - now;
                if (diff <= 0) return "Live";

                const totalHours = Math.floor(diff / (1000 * 60 * 60));
                const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((diff % (1000 * 60)) / 1000);

                return `${totalHours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
            } catch (e) {
                return "Live";
            }
        };

        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [startTime]);

    return <span style={{ fontVariantNumeric: 'tabular-nums', color: timeLeft === "Live" ? "red" : "inherit" }}>{timeLeft}</span>;
};

function Contests() {
    const navigate = useNavigate();
    const location = useLocation();

    const { joinContest } = useContext(ContestContext);
    const { user, fetchUserData } = useContext(UserContext);
    const currentUserId = useMemo(() => {
        const directUserId = user?._id || user?.id;
        if (directUserId) return String(directUserId);

        try {
            const userStr = localStorage.getItem("user");
            if (userStr) {
                const parsedUser = JSON.parse(userStr);
                const parsedUserId = parsedUser?._id || parsedUser?.id || parsedUser?.userId || parsedUser?.user?._id;
                if (parsedUserId) return String(parsedUserId);
            }
        } catch (e) { }

        return String(localStorage.getItem("userId") || localStorage.getItem("_id") || "");
    }, [user]);

    const matchData = useMemo(() => {
        const stateData = location.state?.matchData;
        if (!stateData) return null;

        const finalMatchId = stateData.matchId || stateData.id || stateData._id;

        let normalizedTime = stateData.startTime;
        if (stateData.startTimestamp) {
            normalizedTime = stateData.startTimestamp * 1000;
        }

        return {
            matchId: String(finalMatchId),
            team1: {
                code: stateData.team1?.code || stateData.homeTeam?.shortName || "T1",
                name: stateData.team1?.name || stateData.homeTeam?.name || "Team 1",
                color: stateData.team1?.color || "#333",
                image: stateData.team1?.image || stateData.homeTeam?.logoUrl
            },
            team2: {
                code: stateData.team2?.code || stateData.awayTeam?.shortName || "T2",
                name: stateData.team2?.name || stateData.awayTeam?.name || "Team 2",
                color: stateData.team2?.color || "#333",
                image: stateData.team2?.image || stateData.awayTeam?.logoUrl
            },
            startTime: normalizedTime,
            matchStartTime: stateData.matchStartTime || normalizedTime,
            status: String(stateData.status || "").toLowerCase(),
            lineupOut: Boolean(stateData.lineupOut),
            lineupsOut: Boolean(stateData.lineupsOut),
        };
    }, [location.state]);

    useEffect(() => {
        if (!matchData) navigate("/home");
    }, [matchData, navigate]);

    const [liveContests, setLiveContests] = useState([]);
    const [myContests, setMyContests] = useState([]);
    const [myTeams, setMyTeams] = useState([]);
    const [liveMatchInfo, setLiveMatchInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(location.state?.initialTab || "contests");
    const [joiningId, setJoiningId] = useState(null);

    const [showJoinModal, setShowJoinModal] = useState(false);
    const [showCreateTeamModal, setShowCreateTeamModal] = useState(false);
    const [selectedContest, setSelectedContest] = useState(null);
    // Added state to hold visual calculated bonus logic
    const [paymentBreakdown, setPaymentBreakdown] = useState({ fee: 0, bonusUsed: 0, toPay: 0, hasBalance: false });

    const token = localStorage.getItem("token");

    // 🔥 MAIN DATA FETCHING 🔥
    useEffect(() => {
        if (!matchData?.matchId) return;

        const fetchData = async () => {
            try {
                const token = localStorage.getItem("token");

                let currentUserId = user?._id || user?.id;
                if (!currentUserId) {
                    try {
                        const userStr = localStorage.getItem("user");
                        if (userStr) {
                            const parsedUser = JSON.parse(userStr);
                            currentUserId = parsedUser?._id || parsedUser?.id || parsedUser?.userId || parsedUser?.user?._id;
                        }
                        if (!currentUserId) {
                            currentUserId = localStorage.getItem("userId") || localStorage.getItem("_id");
                        }
                    } catch (e) { }
                }

                const contestsRequest = axios.get(`${CRICKET_API_URL}/api/cricket/contests/${matchData.matchId}`);
                const liveMatchRequest = axios
                    .get(`${CRICKET_API_URL}/api/cricket/match/${matchData.matchId}/live`)
                    .catch(() => null);
                const teamsRequest =
                    token && currentUserId
                        ? axios.get(`${CRICKET_API_URL}/api/cricket/my-teams/${currentUserId}/${matchData.matchId}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        }).catch(() => null)
                        : Promise.resolve(null);

                if (!token || !currentUserId) {
                    console.log("⚠️ User ID abhi bhi nahi mili, My Teams API skip ho gayi.");
                }

                const [contestsRes, liveMatchRes, teamsRes] = await Promise.all([
                    contestsRequest,
                    liveMatchRequest,
                    teamsRequest,
                ]);

                const allContests = contestsRes.data && contestsRes.data.success ? contestsRes.data.data : [];
                setLiveMatchInfo(liveMatchRes?.data || null);

                let newMyTeams = [];
                if (teamsRes && teamsRes.data) {
                    if (Array.isArray(teamsRes.data)) {
                        newMyTeams = teamsRes.data;
                    }
                    else if (teamsRes.data.data) {
                        newMyTeams = teamsRes.data.data;
                    }
                    else if (teamsRes.data.teams) {
                        newMyTeams = teamsRes.data.teams;
                    }
                }

                let newMyContests = [];
                if (currentUserId && allContests.length > 0) {
                    newMyContests = allContests.filter(contest =>
                        contest.participants?.some(p => String(p.userId) === String(currentUserId))
                    );
                }

                const normalizedMyTeams = newMyTeams.map((team) =>
                    normalizeTeamSelection(team),
                );

                setLiveContests(allContests);
                setMyTeams(normalizedMyTeams);
                setMyContests(newMyContests);

            } catch (error) {
                console.error("Error fetching contests/teams:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [matchData?.matchId, token, user]);

    // 🔥 AUTO-OPEN MODAL LOGIC (Option 1: Safe & Transparent) 🔥

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        const contestToJoin = location.state?.autoOpenContest;

        // Wait for user data to be ready
        if (contestToJoin && myTeams.length > 0 && user) {

            // 1. Sabse pehle URL state se request hata do taaki page refresh hone par modal baar-baar na khule
            navigate(location.pathname, {
                replace: true,
                state: { ...location.state, autoOpenContest: null }
            });

            // 2. Chup-chaap wahi Join Modal wapas khol do!
            handleJoinClick(contestToJoin);
        }
    }, [location.state?.autoOpenContest, myTeams, user]);
    /* eslint-enable react-hooks/exhaustive-deps */



    const handleJoinClick = (contest) => {
        if (!token) {
            alert("Please login to join contests.");
            return navigate("/auth");
        }

        if (myTeams.length === 0) {
            setSelectedContest(contest); // Yaad rakhne ke liye ki kis contest ke liye team banani hai
            setShowCreateTeamModal(true); // Apna sundar modal kholo
            return;
        }

        // 🔥 FIX 2: 10% BONUS CALCULATION LOGIC FOR MODAL 🔥
        const entryFee = contest.entryFee || 0;
        const availableBonus = user?.wallet?.bonus || 0;
        const realBalance = (user?.wallet?.deposited || 0) + (user?.wallet?.winnings || 0);

        // Allowed bonus is 10% of entry fee
        const maxBonusAllowed = entryFee * 0.10;
        const bonusToUse = Math.min(availableBonus, maxBonusAllowed);

        const remainingToPay = entryFee - bonusToUse;

        // Can user afford it?
        const hasEnoughBalance = realBalance >= remainingToPay;

        setPaymentBreakdown({
            fee: entryFee,
            bonusUsed: bonusToUse,
            toPay: remainingToPay,
            hasBalance: hasEnoughBalance
        });

        setSelectedContest(contest);
        setShowJoinModal(true);
    };

    const confirmJoinContest = async () => {
        if (!selectedContest || joiningId) return;

        if (!paymentBreakdown.hasBalance) {
            alert("Insufficient Balance! Please Add Cash.");
            return;
        }

        setJoiningId(selectedContest._id);
        const selectedTeam = myTeams[0];

        // 🔥 FIX 1: BULLETPROOF USER EXTRACTION TO STOP `id of null` ERROR 🔥
        let currentUserId = user?._id || user?.id;
        let currentUserName = user?.name || "Player";

        if (!currentUserId) {
            try {
                const userStr = localStorage.getItem("user");
                if (userStr) {
                    const parsedUser = JSON.parse(userStr);
                    currentUserId = parsedUser?._id || parsedUser?.id || parsedUser?.userId || parsedUser?.user?._id;
                    currentUserName = parsedUser?.name || "Player";
                }
            } catch (e) { }
        }

        if (!currentUserId) {
            alert("Session error. Please login again.");
            setJoiningId(null);
            return;
        }

        try {
            const res = await joinContest(
                currentUserId,       // Safe ID passed
                currentUserName,     // Safe Name passed
                selectedContest._id,
                selectedTeam._id,
                matchData.matchId,
                token
            );

            if (res.success) {
                const updatedMyContests = [...myContests, selectedContest];
                setMyContests(updatedMyContests);

                const contestsRes = await axios.get(`${CRICKET_API_URL}/api/cricket/contests/${matchData.matchId}`);
                if (contestsRes.data && contestsRes.data.success) {
                    setLiveContests(contestsRes.data.data);
                }

                setShowJoinModal(false);
                setSelectedContest(null);
                setActiveTab("my-contests");
                await fetchUserData(); // Refresh wallet
            }
        } catch (err) {
            alert(err || "Join Failed.");
        } finally {
            setJoiningId(null);
        }
    };

    const liveLineupLookup = useMemo(() => {
        const byId = {};
        const byKey = {};
        const byName = {};
        const homeTeamCode = matchData?.team1?.code || "T1";
        const awayTeamCode = matchData?.team2?.code || "T2";
        const homePlayers = (liveMatchInfo?.homeTeam?.players || liveMatchInfo?.squads?.homeTeam || []).map((player) => ({
            ...player,
            team: homeTeamCode,
        }));
        const awayPlayers = (liveMatchInfo?.awayTeam?.players || liveMatchInfo?.squads?.awayTeam || []).map((player) => ({
            ...player,
            team: awayTeamCode,
        }));

        [...homePlayers, ...awayPlayers].forEach((player) => {
            const playerId = getPlayerId(player);
            if (playerId) {
                byId[playerId] = player;
            }

            const lookupKey = buildLineupLookupKey(player);
            if (lookupKey) {
                byKey[lookupKey] = player;
            }

            const normalizedName = normalizePlayerName(player?.name);
            if (normalizedName && !byName[normalizedName]) {
                byName[normalizedName] = player;
            }
        });

        return { byId, byKey, byName };
    }, [liveMatchInfo, matchData?.team1?.code, matchData?.team2?.code]);

    if (!matchData) return null;
    const showLineupIndicator = isLineupOutForMatch(liveMatchInfo || matchData);

    // ✅ RENDER CONTESTS
    const renderContestList = (list, isMyContest = false) => {
        const displayList = isMyContest ? list : list.filter(c => !c.isFull);

        if (!displayList || displayList.length === 0) {
            return (
                <div className="text-center py-5 text-muted animate-fade-in">
                    <div className="mb-3 bg-white rounded-circle d-inline-flex p-4 shadow-sm border border-light">
                        <i className="bi bi-trophy fs-1 opacity-25"></i>
                    </div>
                    <p className="fw-bold">{isMyContest ? "You haven't joined any contests yet." : "No contests available right now."}</p>
                </div>
            );
        }

        return displayList.map(contest => {
            const totalSpots = contest.totalSpots || 2;
            const spotsLeft = contest.remainingSpots || 0;
            const filledSpots = totalSpots - spotsLeft;
            const progress = (filledSpots / totalSpots) * 100;
            const isProcessing = joiningId === contest._id;
            const isJoinedContest =
                isMyContest ||
                myContests.some((joinedContest) => String(joinedContest?._id || joinedContest?.id) === String(contest?._id || contest?.id)) ||
                contest.participants?.some((participant) => String(participant?.userId) === String(currentUserId));

            // 🔥 DYNAMIC CALCULATIONS (As per your requirement)
            const prizeBreakup = contest.prizeBreakup || [];

            // 🥇 1st Prize: Breakup se Rank 1 ka amount nikalo
            const firstPrize = prizeBreakup.find(p => p.rankStart === 1)?.prizeAmount ||
                prizeBreakup.find(p => p.rankStart === 1)?.prize || 0;

            // 🏆 Winners %: Last rank in breakup / total spots
            const totalWinnersCount = prizeBreakup.length > 0 ? prizeBreakup[prizeBreakup.length - 1].rankEnd : 0;
            const winPercentage = totalSpots > 0 ? Math.round((totalWinnersCount / totalSpots) * 100) : 0;

            return (
                <div
                    key={contest._id}
                    className="card border-0 shadow-sm mb-3 animate-fade-in contest-card-premium"
                    style={{ borderRadius: "12px", overflow: "hidden", cursor: 'pointer', background: '#fff' }}
                    onClick={(e) => {
                        if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
                            navigate(`/contest-detail/${contest._id}`, { state: { contest, matchData } })
                        }
                    }}
                >
                    {/* Card Header */}
                    <div className="px-3 py-2 d-flex justify-content-between align-items-center border-bottom border-light">
                        <span className="text-muted fw-bold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                            {contest.name || "Mega Contest"}
                        </span>
                        <div className="d-flex gap-2">
                            {contest.isGuaranteed && <span className="badge bg-success bg-opacity-10 text-success border-0 rounded-pill" style={{ fontSize: '9px' }}>Guaranteed</span>}
                            {isMyContest && <span className="badge text-success bg-success bg-opacity-10 px-2 py-1" style={{ fontSize: '9px' }}>JOINED</span>}
                        </div>
                    </div>

                    <div className="card-body p-3">
                        {/* Prize Pool & Join Button Row */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <small className="text-muted d-block mb-1" style={{ fontSize: "11px" }}>Prize Pool</small>
                                <h4 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>₹{contest.prizePool?.toLocaleString()}</h4>
                            </div>

                            <button
                                className={`btn px-4 py-1 fw-bold rounded-pill shadow-sm transition-all
                                    ${isJoinedContest
                                        ? 'btn-outline-success border-2'
                                        : spotsLeft <= 0
                                            ? 'bg-secondary text-white border-0 opacity-75'
                                            : 'text-white border-0'
                                    }`}
                                onClick={(e) => {
                                    e.stopPropagation();

                                    if (isJoinedContest) {
                                        navigate(`/contest-detail/${contest._id}`, { state: { contest, matchData } });
                                        return;
                                    }

                                    if (!isProcessing && spotsLeft > 0) {
                                        handleJoinClick(contest);
                                    }
                                }}
                                disabled={!isJoinedContest && (isProcessing || spotsLeft <= 0)}
                                style={
                                    !isJoinedContest && spotsLeft > 0
                                        ? { background: "linear-gradient(135deg, #28a745 0%, #1e7e34 100%)", minWidth: '85px' }
                                        : { minWidth: '85px' }
                                }
                            >
                                {isProcessing ? (
                                    <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                    isJoinedContest ? "VIEW" : spotsLeft <= 0 ? "FULL" : `₹${contest.entryFee}`
                                )}
                            </button>
                        </div>

                        {/* Progress Bar */}
                        <div className="progress mb-2" style={{ height: "4px", borderRadius: "10px", backgroundColor: "#f0f0f0" }}>
                            <div
                                className="progress-bar"
                                role="progressbar"
                                style={{
                                    width: `${progress}%`,
                                    transition: 'width 0.4s ease',
                                    background: progress > 80 ? "#ffc107" : "#dc3545",
                                    borderRadius: "10px"
                                }}
                            ></div>
                        </div>

                        {/* Spots Info */}
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-danger fw-bold" style={{ fontSize: "11px" }}>
                                {spotsLeft <= 0 ? "Contest Full" : `${spotsLeft.toLocaleString()} spots left`}
                            </small>
                            <small className="text-muted fw-medium" style={{ fontSize: "11px" }}>
                                {totalSpots.toLocaleString()} spots
                            </small>
                        </div>
                    </div>

                    {/* 🟢 PREMIUM FOOTER: Dynamic 1st Prize & Winner % */}
                    <div className="px-3 py-2 bg-light border-top d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center">
                            <i className="bi bi-trophy text-warning me-1" style={{ fontSize: '12px' }}></i>
                            <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>
                                1st: ₹{firstPrize.toLocaleString()}
                            </small>
                        </div>
                        <div className="d-flex align-items-center">
                            <i className="bi bi-award text-primary me-1" style={{ fontSize: '12px' }}></i>
                            <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>
                                {winPercentage}% Winners
                            </small>
                        </div>
                        <div className="ms-auto">
                            <span className="badge rounded-circle border text-muted fw-normal p-0 d-flex align-items-center justify-content-center" style={{ width: '18px', height: '18px', fontSize: '9px' }}>M</span>
                        </div>
                    </div>

                    <style>{`
                        .contest-card-premium { transition: transform 0.2s; }
                        .contest-card-premium:active { transform: scale(0.97); }
                    `}</style>
                </div>
            );
        });
    };

    return (
        <div style={{ paddingBottom: "100px", paddingTop: "70px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "Roboto, sans-serif" }}>
            <Navbar />

            {/* === STICKY MATCH HEADER (MAROON THEME) === */}
            <div className="sticky-top" style={{ top: "70px", zIndex: 1010 }}>
                <div className="py-2 px-3 text-white shadow-sm" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)" }}>
                    <div className="d-flex align-items-center gap-2">
                        <BackButton className="text-white" />
                        <div className="flex-grow-1 text-center pe-4">
                            <small className="d-block text-white-50 fw-bold text-uppercase" style={{ fontSize: '9px', letterSpacing: "0.5px" }}>
                                {matchData.team1.name} vs {matchData.team2.name}
                            </small>
                            <div className="d-flex align-items-center justify-content-center gap-2 mt-1">
                                <span className="fw-bold" style={{ fontSize: "14px" }}>{matchData.team1.code}</span>
                                <div className="badge bg-white text-danger fw-bold shadow-sm d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "10px", borderRadius: "4px" }}>
                                    <i className="bi bi-clock-fill"></i>
                                    <MatchTimer startTime={matchData.matchStartTime || matchData.startTime} />
                                </div>
                                {showLineupIndicator && (
                                    <div className="badge bg-white text-success fw-bold shadow-sm d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: "10px", borderRadius: "4px" }}>
                                        <span className="opacity-blink" style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#198754', display: 'inline-block' }}></span>
                                        <span>LINEUP OUT</span>
                                    </div>
                                )}
                                <span className="fw-bold" style={{ fontSize: "14px" }}>{matchData.team2.code}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* === TABS (Dream11 Style with Red Line) === */}
                <div className="bg-white shadow-sm border-bottom">
                    <div className="d-flex text-center">
                        <div className="flex-grow-1 py-3 position-relative" style={{ cursor: "pointer", color: activeTab === "contests" ? "#800000" : "#64748b" }} onClick={() => setActiveTab("contests")}>
                            <span className="fw-bold" style={{ fontSize: "13px" }}>Contests</span>
                            {activeTab === "contests" && <div className="position-absolute bottom-0 start-50 translate-middle-x bg-danger" style={{ height: '3px', width: '60%', borderRadius: '3px 3px 0 0' }} />}
                        </div>
                        <div className="flex-grow-1 py-3 position-relative" style={{ cursor: "pointer", color: activeTab === "my-contests" ? "#800000" : "#64748b" }} onClick={() => setActiveTab("my-contests")}>
                            <span className="fw-bold" style={{ fontSize: "13px" }}>My Contests ({myContests.length})</span>
                            {activeTab === "my-contests" && <div className="position-absolute bottom-0 start-50 translate-middle-x bg-danger" style={{ height: '3px', width: '60%', borderRadius: '3px 3px 0 0' }} />}
                        </div>
                        <div className="flex-grow-1 py-3 position-relative" style={{ cursor: "pointer", color: activeTab === "my-teams" ? "#800000" : "#64748b" }} onClick={() => setActiveTab("my-teams")}>
                            <span className="fw-bold" style={{ fontSize: "13px" }}>My Teams ({myTeams.length})</span>
                            {activeTab === "my-teams" && <div className="position-absolute bottom-0 start-50 translate-middle-x bg-danger" style={{ height: '3px', width: '60%', borderRadius: '3px 3px 0 0' }} />}
                        </div>
                    </div>
                </div>
            </div>

            {/* === CONTENT === */}
            <div className="container-fluid px-3 py-3">

                {loading && (
                    <div className="text-center py-5 mt-5">
                        <div className="spinner-border" style={{ color: "#800000" }} role="status"></div>
                    </div>
                )}

                {!loading && activeTab === "contests" && renderContestList(liveContests, false)}
                {!loading && activeTab === "my-contests" && renderContestList(myContests, true)}

                {/* === MY TEAMS LIST === */}
                {!loading && activeTab === "my-teams" && (
                    myTeams.length > 0 ? (
                        myTeams.map((team, index) => {
                            const captain = getPlayerById(team.players, team.captainId);
                            const viceCaptain = getPlayerById(team.players, team.viceCaptainId || team.vcId);
                            const roleCounts = getRoleCounts(team.players);
                            const isLineupOut =
                                showLineupIndicator ||
                                matchData?.lineupOut === true ||
                                matchData?.lineupsOut === true;
                            const { playingCount, notPlayingCount } = (team.players || []).reduce(
                                (counts, player) => {
                                    const matchedLivePlayer =
                                        getMatchedLiveLineupPlayer(player, liveLineupLookup) || player;
                                    const lineupStatus = getResolvedLineupStatus(matchedLivePlayer);

                                    if (lineupStatus === "PLAYING" || lineupStatus === "SUBSTITUTE") {
                                        counts.playingCount += 1;
                                    } else if (lineupStatus === "NOT_PLAYING") {
                                        counts.notPlayingCount += 1;
                                    }

                                    return counts;
                                },
                                { playingCount: 0, notPlayingCount: 0 },
                            );
                            const previewPath = team._id || team.id ? `/team-preview/${team._id || team.id}` : "/team-preview";
                            const previewState = {
                                selectedTeam: team,
                                team,
                                players: team.players,
                                captainId: team.captainId,
                                viceCaptainId: team.viceCaptainId,
                                matchData,
                            };
                            const teamLabel = team.teamName || `Team ${team.teamNumber || index + 1}`;
                            const playerCount = (team.players || []).length;
                            const handleEditClick = (event) => {
                                event.stopPropagation();
                                navigate("/create-team", {
                                    state: {
                                        matchData,
                                        selectedPlayers: team.players,
                                        editMode: true,
                                        teamId: team._id || team.id,
                                        captainId: team.captainId,
                                        viceCaptainId: team.viceCaptainId,
                                        team,
                                    },
                                });
                            };

                            return (
                                <div
                                    key={team._id || team.id || index}
                                    className="bg-white rounded-b-lg rounded-t border-t-4 border-green-500 shadow-sm p-4 mb-4 animate-fade-in"
                                    style={{
                                        borderTop: "4px solid #22c55e",
                                        borderRadius: "0.95rem",
                                        boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => navigate(previewPath, { state: previewState })}
                                >
                                    <div className="d-flex justify-content-between align-items-start gap-3">
                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                            <div className="fw-bold text-dark text-uppercase" style={{ fontSize: "15px", letterSpacing: "0.4px" }}>
                                                {teamLabel}
                                            </div>
                                            {isLineupOut && (
                                                <div className="d-flex flex-wrap gap-2 mt-2">
                                                    <span
                                                        className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-bold"
                                                        style={{ fontSize: "10px", backgroundColor: "#f0fdf4", color: "#15803d" }}
                                                    >
                                                        {playingCount} Playing
                                                    </span>
                                                    <span
                                                        className="d-inline-flex align-items-center px-2 py-1 rounded-pill fw-bold"
                                                        style={{ fontSize: "10px", backgroundColor: "#fef2f2", color: "#dc2626" }}
                                                    >
                                                        {notPlayingCount} Not Playing
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="d-flex align-items-center gap-2 flex-shrink-0">
                                            <button
                                                type="button"
                                                className="btn btn-link p-0 text-secondary d-flex align-items-center justify-content-center"
                                                style={{ width: "32px", height: "32px", textDecoration: "none" }}
                                                onClick={handleEditClick}
                                                aria-label={`Edit ${teamLabel}`}
                                            >
                                                <i className="bi bi-pencil-fill" style={{ fontSize: "15px" }}></i>
                                            </button>
                                            <span
                                                className="bg-gray-900 text-white px-3 py-1 rounded-full text-xs fw-bold d-inline-flex align-items-center justify-content-center"
                                                style={{ backgroundColor: "#111827", borderRadius: "999px", fontSize: "11px", minWidth: "64px" }}
                                            >
                                                {playerCount}/11
                                            </span>
                                        </div>
                                    </div>

                                    <div className="d-flex gap-3 mt-4">
                                        <div
                                            className="flex-1 bg-green-50 border border-green-200 rounded-lg p-3 relative d-flex align-items-center gap-3"
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                backgroundColor: "#f0fdf4",
                                                borderColor: "#bbf7d0",
                                                borderRadius: "0.9rem",
                                            }}
                                        >
                                            <span
                                                className="position-absolute top-0 end-0 d-flex align-items-center justify-content-center text-white fw-bold rounded-circle"
                                                style={{
                                                    top: "0.65rem",
                                                    right: "0.65rem",
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: "#16a34a",
                                                    fontSize: "11px",
                                                }}
                                            >
                                                C
                                            </span>
                                            <div className="w-100">
                                                <div
                                                    className="fw-bold text-uppercase mb-2"
                                                    style={{ fontSize: "10px", letterSpacing: "0.08em", color: "#16a34a" }}
                                                >
                                                    Captain
                                                </div>
                                                <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                                    <img
                                                        src={captain?.image || DEFAULT_PLAYER_IMAGE}
                                                        alt={captain?.name || "Captain"}
                                                        onError={handlePlayerImageError}
                                                        className="rounded-circle bg-white flex-shrink-0"
                                                        style={{
                                                            width: "52px",
                                                            height: "52px",
                                                            objectFit: "cover",
                                                            objectPosition: "top",
                                                            border: "2px solid #22c55e",
                                                        }}
                                                    />
                                                    <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                                                        <div className="fw-bold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                                            {captain?.name || "Captain"}
                                                        </div>
                                                        <div className="text-muted mt-1 text-truncate" style={{ fontSize: "11px" }}>
                                                            {captain?.role || "Player"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            className="flex-1 bg-blue-50 border border-blue-200 rounded-lg p-3 relative d-flex align-items-center gap-3"
                                            style={{
                                                flex: 1,
                                                minWidth: 0,
                                                backgroundColor: "#eff6ff",
                                                borderColor: "#bfdbfe",
                                                borderRadius: "0.9rem",
                                            }}
                                        >
                                            <span
                                                className="position-absolute top-0 end-0 d-flex align-items-center justify-content-center text-white fw-bold rounded-circle"
                                                style={{
                                                    top: "0.65rem",
                                                    right: "0.65rem",
                                                    width: "28px",
                                                    height: "28px",
                                                    backgroundColor: "#2563eb",
                                                    fontSize: "10px",
                                                }}
                                            >
                                                VC
                                            </span>
                                            <div className="w-100">
                                                <div
                                                    className="fw-bold text-uppercase mb-2"
                                                    style={{ fontSize: "10px", letterSpacing: "0.08em", color: "#2563eb" }}
                                                >
                                                    Vice Captain
                                                </div>
                                                <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                                                    <img
                                                        src={viceCaptain?.image || DEFAULT_PLAYER_IMAGE}
                                                        alt={viceCaptain?.name || "Vice Captain"}
                                                        onError={handlePlayerImageError}
                                                        className="rounded-circle bg-white flex-shrink-0"
                                                        style={{
                                                            width: "52px",
                                                            height: "52px",
                                                            objectFit: "cover",
                                                            objectPosition: "top",
                                                            border: "2px solid #3b82f6",
                                                        }}
                                                    />
                                                    <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
                                                        <div className="fw-bold text-dark text-truncate" style={{ fontSize: "13px" }}>
                                                            {viceCaptain?.name || "Vice Captain"}
                                                        </div>
                                                        <div className="text-muted mt-1 text-truncate" style={{ fontSize: "11px" }}>
                                                            {viceCaptain?.role || "Player"}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-flex justify-content-center flex-wrap gap-2 mt-4 pt-3 border-top">
                                        {Object.entries(roleCounts).map(([role, count]) => (
                                            <div
                                                key={role}
                                                className="d-flex align-items-center gap-1 bg-gray-50 border border-gray-200 rounded-full px-2 py-1"
                                                style={{ backgroundColor: "#f9fafb", borderColor: "#e5e7eb", borderRadius: "999px" }}
                                            >
                                                <span className="fw-semibold text-muted" style={{ fontSize: "11px" }}>
                                                    {role}
                                                </span>
                                                <span
                                                    className="bg-gray-900 text-white rounded-full d-flex align-items-center justify-content-center"
                                                    style={{ width: "20px", height: "20px", fontSize: "10px", backgroundColor: "#111827" }}
                                                >
                                                    {count}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-5 text-muted animate-fade-in mt-4">
                            <div className="mb-3 bg-white rounded-circle d-inline-flex p-4 shadow-sm border"><i className="bi bi-shield-shaded fs-1 opacity-25"></i></div>
                            <p className="fw-bold text-dark mb-1">You haven't created any teams yet.</p>
                            <p className="small mb-4">Create your winning combination now!</p>
                            <button className="btn btn-dark rounded-pill px-5 py-2 fw-bold" onClick={() => navigate("/create-team", { state: { matchData } })}>Create Team</button>
                        </div>
                    )
                )}
            </div>

            {/* CREATE TEAM FLOATING BUTTON FIX (UPDATED WITH MAX 1 TEAM LOGIC) */}
            <div className=" d-flex fixed-bottom justify-content-center" style={{ pointerEvents: "none", zIndex: 1050 }}>
                <button
                    className={`btn rounded-pill fw-bold shadow-lg d-flex align-items-center justify-content-center gap-2 text-white border-0 px-4 py-3 animate-slide-up mb-4 ${myTeams.length >= 1 ? 'opacity-75' : ''}`}
                    style={{
                        pointerEvents: "auto",
                        background: myTeams.length >= 1 ? "#6c757d" : "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                        letterSpacing: "1px",
                        fontSize: "15px",
                        width: "90%",
                        maxWidth: "350px"
                    }}
                    disabled={myTeams.length >= 1}
                    onClick={() => navigate("/create-team", { state: { matchData } })}
                >
                    {myTeams.length >= 1 ? (
                        <><i className="bi bi-shield-lock-fill fs-5 text-white"></i> MAX 1 TEAM ALLOWED</>
                    ) : (
                        <><i className="bi bi-plus-circle fs-5 text-white"></i> CREATE TEAM</>
                    )}
                </button>
            </div>

            {/* ✅ JOIN CONFIRMATION MODAL WITH BONUS LOGIC (PREMIUM THEME) */}
            {showJoinModal && selectedContest && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1060, backdropFilter: "blur(4px)" }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered px-3">
                        <div className="modal-content border-0 shadow-lg animate-slide-up" style={{ borderRadius: "16px", overflow: "hidden" }}>

                            {/* Modal Header (Maroon Theme) */}
                            <div className="modal-header border-0 pb-3 pt-4 px-4 text-white" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", position: "relative" }}>
                                <div className="w-100 text-center">
                                    <p className="modal-title fw-bolder fs-5 mb-1" style={{ letterSpacing: "0.5px" }}> <strong>Confirm Join</strong> </p>
                                    <p className="mb-0 text-white-50 small fw-semibold "><strong className="text-white">You are joining  {selectedContest.name}</strong></p>
                                </div>
                                <div
                                    className="position-absolute d-flex align-items-center justify-content-center bg-white bg-opacity-25 rounded-circle"
                                    style={{ top: "15px", right: "15px", width: "30px", height: "30px", cursor: "pointer", transition: "all 0.2s" }}
                                    onClick={() => setShowJoinModal(false)}
                                >
                                    <i className="bi bi-x-lg fs-6 text-white"></i>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body pt-4 pb-2 px-4 bg-light">
                                <div className="bg-white border p-3 shadow-sm mb-3" style={{ borderRadius: "12px" }}>

                                    {/* Entry Fee */}
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <span className="text-secondary fw-bold" style={{ fontSize: "14px" }}>Entry Fee</span>
                                        <span className="fw-bold text-dark fs-5">₹{paymentBreakdown.fee}</span>
                                    </div>

                                    {/* Bonus Row (If applicable) */}
                                    {paymentBreakdown.bonusUsed > 0 && (
                                        <div className="d-flex justify-content-between align-items-center mb-3 p-2 rounded" style={{ backgroundColor: "#e8f5e9", border: "1px dashed #c3e6cb" }}>
                                            <span className="text-success fw-bold d-flex align-items-center" style={{ fontSize: "13px" }}>
                                                <i className="bi bi-gift-fill me-2 fs-6"></i> Usable Bonus (10%)
                                            </span>
                                            <span className="fw-bold text-success fs-6">- ₹{paymentBreakdown.bonusUsed.toFixed(1)}</span>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    <div className="border-bottom border-dashed my-3" style={{ borderBottomStyle: "dashed", borderColor: "#dee2e6" }}></div>

                                    {/* To Pay */}
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-dark fw-bold" style={{ fontSize: "15px" }}>To Pay</span>
                                        <span className="fw-bolder fs-4" style={{ color: paymentBreakdown.hasBalance ? "#800000" : "#dc3545" }}>
                                            ₹{paymentBreakdown.toPay.toFixed(1)}
                                        </span>
                                    </div>
                                </div>

                                {/* Insufficient Balance Warning */}
                                {!paymentBreakdown.hasBalance && (
                                    <div className="text-center p-2 mb-3 rounded" style={{ backgroundColor: "#fff3f3", border: "1px solid #f5c2c7" }}>
                                        <small className="fw-bold text-danger d-flex align-items-center justify-content-center gap-1">
                                            <i className="bi bi-exclamation-triangle-fill"></i> Insufficient Balance! Please add cash.
                                        </small>
                                    </div>
                                )}

                                <div className="text-center mb-1">
                                    <small className="text-muted" style={{ fontSize: '11px' }}>By joining this contest, you accept our <span className="text-primary" style={{ cursor: "pointer" }}>T&C</span>.</small>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="modal-footer border-top-0 pt-2 pb-4 px-4 bg-light d-flex gap-3">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary fw-bold shadow-sm"
                                    style={{ flex: 1, borderRadius: "8px", padding: "12px 0", fontSize: "14px" }}
                                    onClick={() => setShowJoinModal(false)}
                                >
                                    Cancel
                                </button>

                                {paymentBreakdown.hasBalance ? (
                                    <button
                                        type="button"
                                        className="btn fw-bold text-white shadow-lg position-relative overflow-hidden"
                                        onClick={confirmJoinContest}
                                        disabled={joiningId === selectedContest._id}
                                        style={{
                                            flex: 1.5,
                                            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                            border: "none",
                                            borderRadius: "8px",
                                            padding: "12px 0",
                                            fontSize: "15px",
                                            letterSpacing: "0.5px"
                                        }}
                                    >
                                        {joiningId === selectedContest._id ? (
                                            <><span className="spinner-border spinner-border-sm me-2"></span>Processing</>
                                        ) : (
                                            `PAY ₹${paymentBreakdown.toPay.toFixed(1)}`
                                        )}
                                    </button>
                                ) : (
                                    // 🔥 THE FIX: Navigating to Add Cash with full context state
                                    <button
                                        type="button"
                                        className="btn fw-bold text-white shadow-lg"
                                        style={{ flex: 1.5, background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", border: "none", borderRadius: "8px", padding: "12px 0" }}
                                        onClick={() => {
                                            setShowJoinModal(false);
                                            navigate("/wallet", {
                                                state: {
                                                    autoOpenAdd: true,
                                                    amountNeeded: paymentBreakdown.toPay,
                                                    returnContext: {
                                                        url: location.pathname,  // 🔥 FIX: Exact URL capture karega
                                                        matchData: matchData,
                                                        contestToJoin: selectedContest,
                                                        initialTab: "contests"
                                                    }
                                                }
                                            });
                                        }}
                                    >
                                        <i className="bi bi-wallet2 me-2"></i>ADD CASH
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* === CREATE TEAM ALERT MODAL === */}
            {showCreateTeamModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1070, backdropFilter: "blur(4px)" }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered px-3">
                        <div className="modal-content border-0 shadow-lg animate-slide-up" style={{ borderRadius: "16px", overflow: "hidden" }}>

                            {/* Header */}
                            <div className="modal-header border-0 pb-0 pt-4 px-4 text-center d-block">
                                <div className="mx-auto mb-3 d-flex align-items-center justify-content-center bg-warning bg-opacity-10 rounded-circle" style={{ width: "60px", height: "60px" }}>
                                    <i className="bi bi-shield-plus text-warning fs-1"></i>
                                </div>
                                <h5 className="fw-bold text-dark mb-1">Team Required!</h5>
                            </div>

                            {/* Body */}
                            <div className="modal-body text-center px-4 pt-2 pb-4">
                                <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                                    You haven't created a team for this match yet. Create one now to join the contest!
                                </p>
                            </div>

                            {/* Footer / Buttons */}
                            <div className="modal-footer border-0 p-4 pt-0 d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-light flex-grow-1 fw-bold border"
                                    style={{ borderRadius: "10px", padding: "12px 0" }}
                                    onClick={() => setShowCreateTeamModal(false)}
                                >
                                    CANCEL
                                </button>
                                <button
                                    type="button"
                                    className="btn text-white flex-grow-1 fw-bold shadow-sm"
                                    style={{
                                        background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                                        border: "none",
                                        borderRadius: "10px",
                                        padding: "12px 0"
                                    }}
                                    onClick={() => {
                                        setShowCreateTeamModal(false);
                                        navigate("/create-team", { state: { matchData, contestToJoin: selectedContest } });
                                    }}
                                >
                                    CREATE TEAM
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                .animate-slide-up { animation: slideUp 0.3s ease-out; }
                .card-hover:hover { transform: translateY(-2px); box-shadow: 0 .25rem .5rem rgba(0,0,0,.05)!important; }
                .opacity-blink { animation: opacityBlink 1.2s infinite; }
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes opacityBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.25; } }
                ::-webkit-scrollbar { width: 0; height: 0; }
            `}</style>
        </div>
    );
}

export default Contests;
